import { z } from "zod";
import type {
  AlteracaoDePessoa,
  EnderecoDoBeneficiario,
  MunicipioCandidato,
  PessoaDetalhe,
  PessoaNova,
} from "@/api/contrato";
import { documento as documentoPontuado } from "@/formato";
import { candidatosDaRecusa, errosDoFormulario } from "./recusas";

/*
 * O cedente no painel: o formulário, a validação que espelha a API e os corpos, no
 * padrão de `telas/planos/plano.ts`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ A VALIDAÇÃO DAQUI ESPELHA A DA API, E NÃO A SUBSTITUI.                   │
 * │                                                                          │
 * │ `prepararEndereco` e `prepararContato` decidem. Aqui só se evita a       │
 * │ viagem por um campo em branco. O documento não passa por conferência de  │
 * │ dígito de propósito: quem diz o que é documento é                        │
 * │ `domain.VerificarDocumento` (emenda ao doc 06a §5).                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * As chaves SÃO os nomes da API, inclusive os aninhados: `errosDoFormulario` reparte a
 * recusa por esses nomes, e uma tradução no meio seria um mapa para desatualizar.
 */
export interface ValoresDoCedente {
  documento: string;
  razao_social: string;
  nome_fantasia: string;
  email: string;
  telefone: string;
  "endereco.cep": string;
  "endereco.logradouro": string;
  "endereco.numero": string;
  "endereco.complemento": string;
  "endereco.bairro": string;
  "endereco.uf": string;
  "endereco.cidade": string;
  /** Só existe depois de uma recusa com candidatos, ou no cedente já gravado. */
  "endereco.codigo_ibge": string;
}

export type CampoDoCedente = keyof ValoresDoCedente;
export type ErrosDoCedente = Partial<Record<CampoDoCedente, string | undefined>>;

export const VALORES_INICIAIS: ValoresDoCedente = {
  documento: "",
  razao_social: "",
  nome_fantasia: "",
  email: "",
  telefone: "",
  "endereco.cep": "",
  "endereco.logradouro": "",
  "endereco.numero": "",
  "endereco.complemento": "",
  "endereco.bairro": "",
  "endereco.uf": "",
  "endereco.cidade": "",
  "endereco.codigo_ibge": "",
};

export const UFS = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA",
  "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO",
] as const;

/*
 * Os limites do §6.1 de `docs/plugboleto-api.md`. Telefone e CEP contam dígitos lá, e a
 * API descarta a pontuação: o campo deixa caber `(71) 99999-0000` e `40.060-001`.
 */
export const TAMANHO_MAXIMO = {
  razao_social: 200,
  nome_fantasia: 200,
  email: 150,
  telefone: 15,
  "endereco.cep": 10,
  "endereco.logradouro": 200,
  "endereco.numero": 10,
  "endereco.complemento": 100,
  "endereco.bairro": 100,
} as const;

/** O cedente gravado, como o formulário o mostra para edição. */
export function valoresDe(pessoa: PessoaDetalhe): ValoresDoCedente {
  const { endereco } = pessoa;

  return {
    documento: documentoPontuado(pessoa.documento),
    razao_social: pessoa.razao_social,
    nome_fantasia: pessoa.nome_fantasia ?? "",
    email: pessoa.email ?? "",
    telefone: pessoa.telefone ?? "",
    "endereco.cep": endereco.cep,
    "endereco.logradouro": endereco.logradouro,
    "endereco.numero": endereco.numero,
    "endereco.complemento": endereco.complemento ?? "",
    "endereco.bairro": endereco.bairro,
    "endereco.uf": endereco.uf,
    "endereco.cidade": endereco.cidade,
    "endereco.codigo_ibge": endereco.codigo_ibge,
  };
}

function soDigitos(texto: string): string {
  return texto.replace(/\D/g, "");
}

const obrigatorio = (mensagem: string) => z.string().trim().min(1, mensagem);
const opcional = z.string().trim();

function emailAceito(email: string): boolean {
  if (email === "") return true;
  const partes = email.split("@");
  return partes.length === 2 && partes.every((parte) => parte !== "") && !/\s/.test(email);
}

function telefoneAceito(telefone: string): boolean {
  const digitos = soDigitos(telefone).length;
  return digitos === 0 || digitos === 10 || digitos === 11;
}

const ESQUEMA_DO_CADASTRO = z.object({
  razao_social: obrigatorio("Informe a razão social."),
  nome_fantasia: opcional,
  email: opcional.refine(emailAceito, "E-mail inválido."),
  telefone: opcional.refine(telefoneAceito, "Telefone com DDD: 10 ou 11 dígitos."),
});

const ESQUEMA_DO_ENDERECO = z.object({
  "endereco.cep": opcional.refine((cep) => soDigitos(cep).length === 8, "O CEP tem oito dígitos."),
  "endereco.logradouro": obrigatorio("Informe o logradouro."),
  "endereco.numero": obrigatorio("Informe o número."),
  "endereco.complemento": opcional,
  "endereco.bairro": obrigatorio("Informe o bairro."),
  "endereco.uf": obrigatorio("Escolha a UF."),
  "endereco.cidade": obrigatorio("Informe a cidade."),
  "endereco.codigo_ibge": z.string(),
});

const ESQUEMA_DA_CRIACAO = ESQUEMA_DO_CADASTRO.extend(ESQUEMA_DO_ENDERECO.shape).extend({
  documento: obrigatorio("Informe o CNPJ ou CPF."),
});

export type ResultadoDaValidacao<C> = { ok: true; corpo: C } | { ok: false; erros: ErrosDoCedente };

function errosDe(falha: z.ZodError): ErrosDoCedente {
  const erros: ErrosDoCedente = {};
  for (const problema of falha.issues) {
    const campo = problema.path[0] as CampoDoCedente;
    erros[campo] ??= problema.message;
  }
  return erros;
}

function enderecoDe(
  endereco: z.output<typeof ESQUEMA_DO_ENDERECO>,
): EnderecoDoBeneficiario {
  const complemento = endereco["endereco.complemento"];
  const codigoIBGE = endereco["endereco.codigo_ibge"];

  return {
    logradouro: endereco["endereco.logradouro"],
    numero: endereco["endereco.numero"],
    bairro: endereco["endereco.bairro"],
    cidade: endereco["endereco.cidade"],
    uf: endereco["endereco.uf"],
    cep: endereco["endereco.cep"],
    ...(complemento !== "" && { complemento }),
    ...(codigoIBGE !== "" && { codigo_ibge: codigoIBGE }),
  };
}

/**
 * O corpo do `POST`. O documento vai como está na tela, pontuado: a API o normaliza, e
 * normalizar aqui seria uma segunda regra. Opcional vazio fica de fora: uma empresa sem
 * nome fantasia não tem um nome fantasia chamado `""`.
 */
export function validarCriacao(valores: ValoresDoCedente): ResultadoDaValidacao<PessoaNova> {
  const resultado = ESQUEMA_DA_CRIACAO.safeParse(valores);
  if (!resultado.success) return { ok: false, erros: errosDe(resultado.error) };

  const { documento, razao_social, nome_fantasia, email, telefone } = resultado.data;

  return {
    ok: true,
    corpo: {
      documento,
      razao_social,
      endereco: enderecoDe(resultado.data),
      ...(nome_fantasia !== "" && { nome_fantasia }),
      ...(email !== "" && { email }),
      ...(telefone !== "" && { telefone }),
    },
  };
}

const CAMPOS_DO_ENDERECO = [
  "endereco.cep",
  "endereco.logradouro",
  "endereco.numero",
  "endereco.complemento",
  "endereco.bairro",
  "endereco.uf",
  "endereco.cidade",
  "endereco.codigo_ibge",
] as const;

function comparavel(campo: CampoDoCedente, valor: string): string {
  return campo === "endereco.cep" ? soDigitos(valor) : valor.trim();
}

/**
 * O corpo do `PATCH`: só o que mudou, e vazio em opcional grava `NULL`. O endereço é
 * substituído inteiro, e só vai se algum campo dele mudou.
 *
 * Endereço intocado também não é validado: o cedente anterior ao endereço tipado o tem
 * vazio, e precisa poder corrigir o telefone sem preencher o resto. Quem cobra é a
 * pendência `sem_endereco`.
 *
 * Corpo vazio é "nada a gravar": `PATCH` sem chave é `400 invalid_body`, e quem chama
 * fecha o modal sem ir à API.
 */
export function validarEdicao(
  valores: ValoresDoCedente,
  antes: PessoaDetalhe,
): ResultadoDaValidacao<AlteracaoDePessoa> {
  const gravados = valoresDe(antes);
  const tocouEndereco = CAMPOS_DO_ENDERECO.some(
    (campo) => comparavel(campo, valores[campo]) !== comparavel(campo, gravados[campo]),
  );

  const cadastro = ESQUEMA_DO_CADASTRO.safeParse(valores);
  const endereco = ESQUEMA_DO_ENDERECO.safeParse(valores);
  const enderecoRecusado = tocouEndereco && !endereco.success;

  if (!cadastro.success || enderecoRecusado) {
    return {
      ok: false,
      erros: {
        ...(cadastro.success ? {} : errosDe(cadastro.error)),
        ...(enderecoRecusado ? errosDe(endereco.error) : {}),
      },
    };
  }

  const { razao_social, nome_fantasia, email, telefone } = cadastro.data;
  const corpo: AlteracaoDePessoa = {};

  if (razao_social !== gravados.razao_social) corpo.razao_social = razao_social;
  if (nome_fantasia !== gravados.nome_fantasia) corpo.nome_fantasia = nome_fantasia;
  if (email !== gravados.email) corpo.email = email;
  if (soDigitos(telefone) !== soDigitos(gravados.telefone)) corpo.telefone = telefone;
  if (tocouEndereco && endereco.success) corpo.endereco = enderecoDe(endereco.data);

  return { ok: true, corpo };
}

/** Os nomes da API que o formulário sempre mostra. */
const CAMPOS_DA_TELA: readonly CampoDoCedente[] = [
  "documento",
  "razao_social",
  "nome_fantasia",
  "email",
  "telefone",
  "endereco.cep",
  "endereco.logradouro",
  "endereco.numero",
  "endereco.complemento",
  "endereco.bairro",
  "endereco.uf",
  "endereco.cidade",
];

export interface RecusaDoCedente {
  erros: ErrosDoCedente;
  /** O que nenhum campo mostrado recebe, para o rodapé do modal. */
  geral: string | undefined;
  candidatos: readonly MunicipioCandidato[];
}

/**
 * Reparte a recusa da API entre os campos.
 *
 * `municipio_nao_resolvido` aponta `endereco.cidade`, e a saída dele é a seleção de
 * candidatos: o erro vai para ela, que é onde o operador age e para onde o foco vai.
 * Recusa sem candidatos não tira a seleção que já está na tela.
 */
export function recusaDoCedente(
  falha: unknown,
  candidatosNaTela: readonly MunicipioCandidato[],
): RecusaDoCedente {
  const novos = candidatosDaRecusa(falha);
  const candidatos = novos.length > 0 ? novos : candidatosNaTela;
  const campos: readonly CampoDoCedente[] =
    candidatos.length > 0 ? [...CAMPOS_DA_TELA, "endereco.codigo_ibge"] : CAMPOS_DA_TELA;

  const { porCampo, geral } = errosDoFormulario(falha, campos);
  if (novos.length === 0) return { erros: porCampo, geral, candidatos };

  const { "endereco.cidade": daCidade, ...demais } = porCampo;
  return { erros: { ...demais, "endereco.codigo_ibge": daCidade }, geral, candidatos };
}

/**
 * Se dois documentos são o mesmo, ignorando a pontuação.
 *
 * Comparar é o que separa o cedente certo: a busca é por prefixo, e um CPF é prefixo de
 * um CNPJ que comece com os mesmos onze dígitos.
 */
export function mesmoDocumento(a: string, b: string): boolean {
  const significativos = (documento: string) =>
    documento.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();

  return significativos(a) === significativos(b);
}
